// React
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";

// @tanstack/react-query
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Third Party
import moment from "moment";

// MUI
import { Grid, Stack } from "@mui/material";

// API
import { deleteSpiffSetup } from "@Motivators/api/spiffs";

// Custom Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";

// Contexts
import { useMotivators } from "@Motivators/context/Common/MotivatorsContext";
import { MotivatorsSectionProvider } from "@Motivators/context/Common/MotivatorsSectionContext";

// Services
import actions from "@/Constants/actionsList";

// Components
import EmptyBody from "@Layouts/components/Basics/EmptyBody";
import RoundedBox from "@Layouts/components/Basics/RoundedBox";
import SmallActionButton from "@Layouts/components/Buttons/SmallActionButton";

import SpiffDefinitionDataTable from "@Motivators/components/Spiffs/Tables/SpiffDefinitionDataTable";
import HeaderMotivatorContainer from "@Motivators/layouts/components/HeaderMotivatorContainer";
import SpiffEntrySectionLoader from "@Motivators/layouts/components/Skeletons/SpiffEntrySectionLoader";

// Assets
import empty from "@/assets/images/commons/empty.png";
import useCacheCleaner from "@/Common/hooks/CacheCleaner";

// Constants
import { ID_NEW_SETUP } from "@Motivators/constants";

// Helpers
import { extractLeafKeys } from "@/Common/GenericTree/utils";
import {
	canAddItems,
	canUserEditMotivators,
	defineTypeEffetiveDate,
	determineDate,
	handleUpdateEffectiveDateTime,
	handleUpdateTitle,
	isNewItem,
	onMutateTitle,
} from "@/Motivators/helpers";
import { useLocationCycles } from "@/Washouts/hooks/common/useLocationCycles";
import { isEmpty } from "lodash";
import useSpiffAwardsSubsForSetup from "./Tables/hooks/useSpiffAwardsSubsForSetup";

function ContentRenderer(props) {
	// Hooks
	const { useAuth } = useAuthorization();
	const queryClient = useQueryClient();
	const {
		locationId,
		identifiers: { department, title: newTitleDefault, attribute },
		mutations,
		motivatorType,
		isLoading,
		setIsLoading,
		subscriptions,
		selection: selectedItem,
		setSelection,
	} = useMotivators();
	const { isLoadingData, data: spiffSetup, createSetup, hasSpecialEditing } = props;

	const [paramsHeader, setParamsHeader] = useState({ title: newTitleDefault, from: null, to: null });

	const [loadingDateRange, setLoadingDateRange] = useState({
		from: false,
		to: false,
	});

	const [commissionCycleId, spiffId] = selectedItem ? extractLeafKeys(selectedItem._id) : [];

	const { data: commissionCycles } = useLocationCycles();

	useSpiffAwardsSubsForSetup({ locationId, commissionCycleId, spiffId });

	const commissionCycle = useMemo(() => {
		if (!commissionCycleId) return null;
		return commissionCycles?.find((cc) => cc._id === commissionCycleId);
	}, [commissionCycleId, commissionCycles]);

	// Permissions
	const EDIT_SALES_SPIFF_DEFINITIONS = useAuth(actions.EDIT_SALES_SPIFF_DEFINITIONS);
	const EDIT_SERVICE_SPIFF_DEFINITIONS = useAuth(actions.EDIT_SERVICE_SPIFF_DEFINITIONS);
	const MASTER_PERMISSION = canUserEditMotivators(hasSpecialEditing, commissionCycle?.status);

	const hasEditPermission = motivatorType === "spiffSetup" ? EDIT_SALES_SPIFF_DEFINITIONS : EDIT_SERVICE_SPIFF_DEFINITIONS;

	const enableDeleteSpiff = useMemo(() => {
		if (!spiffSetup) return false;
		return hasEditPermission && (moment().isBefore(spiffSetup.effectiveDateFrom) || isEmpty(spiffSetup.items));
	}, [spiffSetup]);

	/******************************************************* MUTATIONS *****************************************************/
	const { mutate: mutateTitle } = useMutation({
		mutationKey: ["updateSpiffSetupTitle"],
		mutationFn: (title) => {
			return mutations.updateSetupTitle({
				locationId,
				title,
				department,
				commissionCycleId,
				spiffId,
			});
		},
		onMutate: (title) => onMutateTitle(queryClient, [`${motivatorType}Entry`, spiffId, locationId], title),
		onError: (_error, _variables, context) => {
			queryClient.setQueryData([`${motivatorType}Entry`, spiffId, locationId], context.previousData);
		},
	});

	const { mutate: mutateDate } = useMutation({
		mutationKey: [`${motivatorType}Entry`, spiffId, locationId],
		mutationFn: (input) => {
			return mutations.updateEffectiveDate({
				locationId,
				department,
				commissionCycleId,
				spiffId,
				effectiveDate: defineTypeEffetiveDate(input.type),
				effectiveDateValue: input.dateValue,
			});
		},
	});

	const { mutate: mutateDeleteSpiffSetup } = useMutation({
		mutationKey: ["deleteSpiffSetup"],
		mutationFn: (input) => deleteSpiffSetup(input),
	});

	/******************************************************** HANDLERS *****************************************************/
	const onEditTitle = (title) => {
		handleUpdateTitle({
			title,
			setParamsHeader,
			titleDefault: newTitleDefault,
			paramsAction: paramsHeader,
			selectedItemId: spiffId,
			commissionCycleId,
			setLoading: setIsLoading,
			mutateFunction: mutateTitle,
			createAction: createSetup,
		});
	};

	const onEditEffectiveDate = (dateValue, type) => {
		handleUpdateEffectiveDateTime({
			dateValue,
			type,
			setParamsHeader,
			titleDefault: newTitleDefault,
			paramsAction: paramsHeader,
			selectedItemId: spiffId,
			commissionCycleId,
			setLoading: setLoadingDateRange,
			mutateFunction: mutateDate,
			createAction: createSetup,
		});
	};

	const removeSpiffSetup = () => {
		if (spiffId === ID_NEW_SETUP) {
			const sidebarData = queryClient.getQueryData([`${motivatorType}Sidebar`, locationId]);

			const newTree =
				sidebarData?.tree?.map((year) => ({
					...year,
					list: year.list?.map((month) => {
						if (month._id === commissionCycleId) {
							return {
								...month,
								list: (month.list || []).filter((item) => item._id !== selectedItem._id),
							};
						}
						return month;
					}),
				})) || [];

			queryClient.setQueryData([`${motivatorType}Sidebar`, locationId], {
				...sidebarData,
				tree: newTree,
			});

			setSelection(null);
		} else {
			try {
				setIsLoading(true);
				mutateDeleteSpiffSetup({ locationId, department, spiffId, commissionCycleId });
			} catch (error) {
				console.log("Error removing List", error);
			}
		}
	};

	const handleDeleteItems = (items, effectiveDate) => {
		const effectiveDateName = effectiveDate.type === "from" ? "effectiveDateFrom" : "effectiveDateTo";
		const effectiveDateValue = effectiveDate.dataTime;
		const input = {
			[effectiveDateName]: effectiveDateValue,
			items,
		};
		setIsLoading(true);
		mutations.updateSetup({
			locationId,
			department,
			input,
			spiffId,
			commissionCycleId,
		});
	};

	useEffect(() => {
		spiffSetup &&
			setParamsHeader({
				from: determineDate(spiffSetup.effectiveDateFrom),
				to: determineDate(spiffSetup.effectiveDateTo),
				title: spiffSetup.title,
			});
	}, [spiffSetup]);

	if (isLoadingData) {
		return <SpiffEntrySectionLoader />;
	}

	if (!selectedItem) {
		return <EmptyBody image={String(empty)} title="No Item selected" bodyHeightClassName={"!h-[510px]"} />;
	}

	return (
		<Grid container spacing={5} component={"section"}>
			<Grid item xs={12} component={"header"}>
				<Stack direction="row" justifyContent={"space-between"}>
					<HeaderMotivatorContainer
						dateRange={{ from: paramsHeader.from, to: paramsHeader.to }}
						setLoadingDateRange={setLoadingDateRange}
						hasEditPermission={hasEditPermission}
						loadingDateRange={loadingDateRange}
						isLoadingData={isLoadingData}
						title={paramsHeader.title}
						defaultTitle={newTitleDefault}
						onEditTitle={onEditTitle}
						onEditEffectiveDate={onEditEffectiveDate}
						onDeleteItems={handleDeleteItems}
						masterPermission={MASTER_PERMISSION}
						spiffSetup={spiffSetup}
					/>
				</Stack>
			</Grid>
			<Grid item xs={12} height={600} className="!pt-[4px]">
				<SpiffDefinitionDataTable
					currentMonth={true}
					locationId={locationId}
					commissionCycle={commissionCycle}
					subscriptions={subscriptions}
					loading={isLoadingData}
					selectedItem={selectedItem}
					spiffSetup={spiffSetup}
					canAddItems={canAddItems(
						hasEditPermission || MASTER_PERMISSION,
						{ from: paramsHeader.from, to: paramsHeader.to },
						spiffId
					)}
					hasEditPermission={hasEditPermission}
					masterPermission={MASTER_PERMISSION}
				/>
			</Grid>
			<Grid item className="absolute right-[50px] bottom-[34px]">
				<SmallActionButton
					disabled={!enableDeleteSpiff}
					buttonText={"Delete Setup"}
					buttonTextConfirm={"Confirm Delete"}
					description={""}
					onClick={removeSpiffSetup}
					loading={isLoading}
				/>
			</Grid>
		</Grid>
	);
}

export default function SpiffSetupSection(props) {
	const { locationId, identifiers, queries, selection, motivatorType } = useMotivators();

	const QUERY_IDENTIFIERS = [`${motivatorType}Entry`, "getSpiffAwards", "recipientSpiffEntry", "userStockNumbers"];
	const { clearCache } = useCacheCleaner();

	const [commissionCycleId, spiffId] = selection ? extractLeafKeys(selection._id) : [];

	/************************************************ QUERIES *************************************************/
	const { data, isLoading: isLoadingData } = useQuery({
		queryKey: [`${motivatorType}Entry`, spiffId, locationId],
		queryFn: async () => {
			return queries.getBodyData({ locationId, spiffId, department: identifiers.department });
		},
		staleTime: Infinity,
		enabled: Boolean(locationId) && Boolean(spiffId) && !isNewItem(spiffId),
	});

	useEffect(() => {
		return () => {
			clearCache(QUERY_IDENTIFIERS);
		};
	}, []);

	return (
		<RoundedBox className="min-h-[630px]" component="section">
			<MotivatorsSectionProvider>
				<ContentRenderer {...props} data={data ?? selection} isLoadingData={isLoadingData} />
			</MotivatorsSectionProvider>
		</RoundedBox>
	);
}

SpiffSetupSection.propTypes = {
	selectedItem: PropTypes.object,
	hasSpecialEditing: PropTypes.bool,
};

ContentRenderer.propTypes = {
	isLoadingData: PropTypes.bool,
	commissionCycleId: PropTypes.string,
	selectedItem: PropTypes.object,
	createSetup: PropTypes.func,
	data: PropTypes.shape({
		title: PropTypes.string,
		items: PropTypes.array,
		effectiveDateFrom: PropTypes.string,
		effectiveDateTo: PropTypes.string,
	}),
	hasSpecialEditing: PropTypes.bool,
};
